import { useState } from "react";
import {
  LayoutDashboard,
  FileText,
  ShoppingCart,
  Wallet,
  Menu,
  X,
  ClipboardCheck,
  MessageSquareQuote,
  Bell,
  Settings,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const sections = [
  {
    label: "Overview",
    items: [
      { name: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
    ],
  },
  {
    label: "Products",
    items: [
      { name: "Policies", icon: FileText, path: "/policies" },
    ],
  },
  {
    label: "Operations",
    items: [
      { name: "Purchases", icon: ShoppingCart, path: "/purchase-management" },
      { name: "Quotes", icon: MessageSquareQuote, path: "/quote-requests" },
      { name: "Claims", icon: ClipboardCheck, path: "/claims" },
    ],
  },
  {
    label: "Finance",
    items: [
      { name: "Earnings", icon: Wallet, path: "/payout" },
    ],
  },
  {
    label: "Account",
    items: [
      { name: "Notifications", icon: Bell, path: "/notifications" },
      { name: "Settings", icon: Settings, path: "/settings" },
    ],
  },
];

const isItemActive = (pathname, path) => {
  if (path === "/settings") {
    return pathname === "/settings" || pathname === "/profile";
  }
  return pathname === path || pathname.startsWith(`${path}/`);
};

export default function Sidebar({ isMobileOpen, onMobileToggle }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { pathname } = useLocation();

  const closeOnMobile = () => {
    if (window.innerWidth < 1024) onMobileToggle();
  };

  return (
    <>
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onMobileToggle}
        />
      )}

      <div
        className={`
          fixed left-0 top-20 z-50 mt-1 flex h-[calc(100vh-5rem)] flex-col bg-black text-white transition-all duration-300 ease-in-out
          lg:relative lg:top-0 lg:mt-0 lg:h-full lg:translate-x-0
          ${isCollapsed ? "w-16 overflow-hidden lg:w-16" : "w-60 lg:w-60"}
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        <div className="flex items-center justify-between border-b border-gray-800 px-3 py-3 lg:hidden">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Menu</span>
          <button
            type="button"
            onClick={onMobileToggle}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:text-white"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className={`flex-1 overflow-y-auto px-3 pb-16 pt-3 ${isCollapsed ? "overflow-x-hidden" : ""}`}>
          {sections.map((section, sectionIndex) => (
            <div key={section.label} className={sectionIndex === 0 ? "" : "mt-4"}>
              {isCollapsed ? (
                sectionIndex > 0 && <div className="mx-2 mb-2 border-t border-gray-800" />
              ) : (
                <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  {section.label}
                </p>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isItemActive(pathname, item.path);
                  return (
                    <div key={item.path} className="group relative">
                      <Link
                        to={item.path}
                        onClick={closeOnMobile}
                        className={`
                          flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors
                          ${active ? "bg-green-500 text-white" : "text-gray-300 hover:bg-gray-800 hover:text-white"}
                          ${isCollapsed ? "justify-center" : "justify-start"}
                        `}
                      >
                        <Icon
                          size={18}
                          className={`${isCollapsed ? "" : "mr-2.5"} ${active ? "text-white" : "text-gray-400 group-hover:text-white"}`}
                        />
                        {!isCollapsed && <span className="whitespace-nowrap">{item.name}</span>}
                      </Link>
                      {isCollapsed && (
                        <div className="pointer-events-none absolute left-full z-50 ml-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-sm text-white opacity-0 transition-opacity group-hover:opacity-100">
                          {item.name}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="absolute bottom-3 right-3 hidden lg:block">
          <button
            type="button"
            onClick={() => setIsCollapsed((collapsed) => !collapsed)}
            className="rounded-lg bg-gray-800 p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <Menu size={18} />
          </button>
        </div>
      </div>
    </>
  );
}
